import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AlertTriangle, Check, ChevronDown, ChevronUp, EyeOff, GripVertical, Pencil, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import AdminCoursesTabs from "../components/AdminCoursesTabs";
import LevelIndicator from "../components/LevelIndicator";
import { THEME_ICONS, themeIcon } from "../lib/courseMeta";
import { confirmDialog } from "../components/ConfirmDialog";
/** Élément déplaçable (glisser par la poignée, ou au clavier : Espace puis flèches). */
function Sortable({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : undefined, position: "relative" };
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? "opacity-70" : ""}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
}
const Grip = (props) => (
  <button type="button" {...props} className="cursor-grab active:cursor-grabbing touch-none p-1 -m-1 text-zinc-600 hover:text-zinc-300" aria-label="Glisser pour réordonner">
    <GripVertical className="w-4 h-4" />
  </button>
);
function ThemeModal({ theme, onClose, onSaved }) {
  const isNew = !theme?.id;
  const [title, setTitle] = useState(theme?.title || "");
  const [description, setDescription] = useState(theme?.description || "");
  const [icon, setIcon] = useState(theme?.icon || "layers");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!title.trim()) { toast.error("Le nom est obligatoire"); return; }
    setBusy(true);
    try {
      const payload = { title: title.trim(), description: description.trim(), icon };
      if (isNew) await api.post("/course-themes", payload);
      else await api.patch(`/course-themes/${theme.id}`, payload);
      toast.success("Thématique enregistrée");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!(await confirmDialog({ title: `Supprimer la thématique « ${theme.title} » ?`, description: "Ses cours ne sont pas supprimés : ils passent dans « Concepts sans thématique »." }))) return;
    try {
      await api.delete(`/course-themes/${theme.id}`);
      toast.success("Thématique supprimée");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  return (
    <div className="fixed inset-0 z-[65] bg-black/70 backdrop-blur-sm grid place-items-center p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-[#121214] shadow-2xl" role="dialog" aria-modal="true" data-testid="theme-modal">
        <div className="flex items-center justify-between px-6 pt-5">
          <h2 className="font-display font-bold text-lg">{isNew ? "Nouvelle thématique" : "Modifier la thématique"}</h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" aria-label="Fermer"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">Nom</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="Ex. : Les classes d'actifs" className="mt-1.5 w-full h-10 rounded-md bg-white/[0.03] border border-white/10 px-3 text-sm" data-testid="theme-modal-title" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">Courte description</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={240} rows={2} placeholder="Une phrase affichée sur la carte de la thématique." className="mt-1.5 w-full rounded-md bg-white/[0.03] border border-white/10 px-3 py-2 text-sm" />
          </label>
          <div>
            <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">Icône</span>
            <div className="mt-1.5 grid grid-cols-8 gap-2">
              {Object.entries(THEME_ICONS).map(([key, Icon]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setIcon(key)}
                  className={`h-10 grid place-items-center rounded-lg border ${icon === key ? "border-orange-500/60 bg-orange-500/10 text-orange-300" : "border-white/10 text-zinc-400 hover:text-white"}`}
                  aria-label={key}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/5 flex items-center gap-3">
          {!isNew && <button type="button" onClick={remove} className="text-sm text-rose-400 hover:text-rose-300 mr-auto">Supprimer</button>}
          <Button variant="outline" className="ml-auto border-white/10 bg-transparent" onClick={onClose}>Annuler</Button>
          <Button className="bg-emerald-500 text-black hover:bg-emerald-300" onClick={save} disabled={busy} data-testid="theme-modal-save">Enregistrer</Button>
        </div>
      </div>
    </div>
  );
}
/** Admin › Cours › Thématiques : ordre des thématiques, ordre de lecture des cours, rangement des concepts. */
export default function AdminCourseThemesPage() {
  const qc = useQueryClient();
  const { data: serverThemes, isLoading } = useQuery({
    queryKey: ["course-themes"],
    queryFn: async () => (await api.get("/course-themes")).data,
  });
  const { data: courses = [] } = useQuery({
    queryKey: ["admin-courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });
  const [themes, setThemes] = useState([]);
  useEffect(() => { if (serverThemes) setThemes(serverThemes); }, [serverThemes]);
  const [open, setOpen] = useState(() => new Set());
  const [modal, setModal] = useState(null); // null | {} (nouvelle) | thématique
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const byId = useMemo(() => Object.fromEntries(courses.map((c) => [c.id, c])), [courses]);
  const concepts = courses.filter((c) => c.category === "concept");
  const placed = new Set(themes.flatMap((t) => t.course_ids));
  const loose = concepts.filter((c) => !placed.has(c.id));
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["course-themes"] });
    qc.invalidateQueries({ queryKey: ["admin-courses"] });
    qc.invalidateQueries({ queryKey: ["courses"] });
  };
  const fail = (e) => { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); refresh(); };
  const saveCourses = async (tid, ids, msg = "Ordre enregistré") => {
    // Un concept n'appartient qu'à une thématique : on le retire localement des autres
    setThemes((ts) => ts.map((t) => (t.id === tid ? { ...t, course_ids: ids } : { ...t, course_ids: t.course_ids.filter((x) => !ids.includes(x)) })));
    try {
      await api.patch(`/course-themes/${tid}`, { course_ids: ids });
      toast.success(msg);
      refresh();
    } catch (e) { fail(e); }
  };
  const onThemesDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(themes, themes.findIndex((t) => t.id === active.id), themes.findIndex((t) => t.id === over.id));
    setThemes(next);
    try {
      await api.put("/course-themes/order", { ids: next.map((t) => t.id) });
      toast.success("Ordre des thématiques enregistré");
      refresh();
    } catch (e) { fail(e); }
  };
  const toggle = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-course-themes">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Admin</div>
          <h1 className="font-display font-black text-3xl">Cours</h1>
        </div>
        <Button onClick={() => setModal({})} className="bg-emerald-500 text-black hover:bg-emerald-300" data-testid="theme-new">
          <Plus className="w-4 h-4 mr-1.5" /> Nouvelle thématique
        </Button>
      </div>
      <AdminCoursesTabs />
      {loose.length > 0 && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <b className="text-amber-200">{loose.length} concept{loose.length > 1 ? "s" : ""} sans thématique.</b>{" "}
            <span className="text-zinc-400">{loose.length > 1 ? "Ils s'affichent" : "Il s'affiche"} dans « Autres concepts » sur le site en attendant. Voir en bas de page.</span>
          </div>
        </div>
      )}
      <p className="mb-3 text-sm text-zinc-500">Glissez les thématiques pour changer leur ordre sur la page Cours. Ouvrez-en une pour ordonner ses cours.</p>
      {isLoading ? (
        <div className="text-zinc-500">Chargement…</div>
      ) : themes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 py-12 text-center text-sm text-zinc-500">
          Aucune thématique pour l'instant. Créez-en une pour commencer à ranger vos concepts.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onThemesDragEnd}>
          <SortableContext items={themes.map((t) => t.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {themes.map((t, i) => {
                const Icon = themeIcon(t.icon);
                const isOpen = open.has(t.id);
                const items = t.course_ids.map((id) => byId[id]).filter(Boolean);
                return (
                  <Sortable key={t.id} id={t.id}>
                    {(handle) => (
                      <div className="rounded-xl border border-white/5 bg-card" data-testid={`admin-theme-${t.slug}`}>
                        <div className="flex items-center gap-3 p-4">
                          <Grip {...handle} />
                          <span className="w-5 text-center text-xs font-semibold text-zinc-500">{i + 1}</span>
                          <div className="w-9 h-9 grid place-items-center rounded-lg bg-orange-500/10 border border-orange-500/25 text-orange-300 shrink-0"><Icon className="w-4 h-4" /></div>
                          <button type="button" onClick={() => toggle(t.id)} className="flex-1 min-w-0 text-left">
                            <div className="font-medium">{t.title}</div>
                            <div className="text-xs text-zinc-500 truncate">{items.length} cours{t.description ? ` · ${t.description}` : ""}</div>
                          </button>
                          <button type="button" onClick={() => setModal(t)} className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-zinc-400 hover:bg-white/5 hover:text-emerald-300">
                            <Pencil className="w-3.5 h-3.5" /> Modifier
                          </button>
                          <button type="button" onClick={() => toggle(t.id)} className="p-1.5 rounded text-zinc-400 hover:bg-white/5" aria-label={isOpen ? "Replier" : "Déplier"} data-testid={`admin-theme-toggle-${t.slug}`}>
                            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                        {isOpen && (
                          <div className="px-4 pb-4 pt-4 border-t border-white/5">
                            <div className="text-xs text-zinc-500 mb-3">Ordre de lecture sur le site. Glissez les cours ou utilisez les flèches.</div>
                            {items.length === 0 ? (
                              <div className="text-sm text-zinc-500 py-3 text-center">Aucun cours pour l'instant.</div>
                            ) : (
                              <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={({ active, over }) => {
                                  if (!over || active.id === over.id) return;
                                  saveCourses(t.id, arrayMove(t.course_ids, t.course_ids.indexOf(active.id), t.course_ids.indexOf(over.id)));
                                }}
                              >
                                <SortableContext items={items.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                                  <div className="space-y-1.5">
                                    {items.map((c, k) => (
                                      <Sortable key={c.id} id={c.id}>
                                        {(h) => (
                                          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-white/5 bg-[#0f0f10]" data-testid={`admin-theme-course-${c.id}`}>
                                            <Grip {...h} />
                                            <span className="w-5 text-center text-xs font-semibold text-zinc-500">{k + 1}</span>
                                            <Link to={`/admin/cours/${c.id}/modifier`} className="flex-1 min-w-0 truncate text-sm text-zinc-200 hover:text-emerald-300">{c.title}</Link>
                                            {c.published === false && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-zinc-500"><EyeOff className="w-3 h-3" /> Brouillon</span>}
                                            <LevelIndicator level={c.level} className="hidden sm:inline-flex" />
                                            {c.is_premium && <span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full border bg-orange-500/10 text-orange-300 border-orange-500/30">Essentiel</span>}
                                            <span className="flex items-center">
                                              <button type="button" disabled={k === 0} onClick={() => saveCourses(t.id, arrayMove(t.course_ids, t.course_ids.indexOf(c.id), t.course_ids.indexOf(c.id) - 1))} className="p-1 rounded text-zinc-500 hover:text-white disabled:opacity-20" aria-label="Monter"><ChevronUp className="w-4 h-4" /></button>
                                              <button type="button" disabled={k === items.length - 1} onClick={() => saveCourses(t.id, arrayMove(t.course_ids, t.course_ids.indexOf(c.id), t.course_ids.indexOf(c.id) + 1))} className="p-1 rounded text-zinc-500 hover:text-white disabled:opacity-20" aria-label="Descendre"><ChevronDown className="w-4 h-4" /></button>
                                              <button type="button" onClick={() => saveCourses(t.id, t.course_ids.filter((x) => x !== c.id), "Cours retiré de la thématique")} className="p-1 rounded text-zinc-500 hover:text-rose-400" aria-label="Retirer de la thématique"><X className="w-4 h-4" /></button>
                                            </span>
                                          </div>
                                        )}
                                      </Sortable>
                                    ))}
                                  </div>
                                </SortableContext>
                              </DndContext>
                            )}
                            {loose.length > 0 && (
                              <div className="mt-3 flex items-center gap-2">
                                <Plus className="w-4 h-4 text-zinc-500" />
                                <select
                                  value=""
                                  onChange={(e) => e.target.value && saveCourses(t.id, [...t.course_ids, e.target.value], "Concept ajouté à la fin de la thématique")}
                                  className="h-9 rounded-md bg-white/[0.03] border border-white/10 px-2 text-sm text-zinc-300"
                                >
                                  <option value="">Ajouter un concept sans thématique…</option>
                                  {loose.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                                </select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Sortable>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div className="mt-10">
        <h2 className="font-display font-bold text-lg">Concepts sans thématique</h2>
        <p className="text-sm text-zinc-500 mt-1 mb-3">Affichés sur le site dans « Autres concepts ». Une fois tous rangés, ce dossier disparaît du site.</p>
        <div className="rounded-xl border border-white/5 bg-card divide-y divide-white/5">
          {loose.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-emerald-300 inline-flex w-full items-center justify-center gap-1.5"><Check className="w-4 h-4" /> Tous les concepts sont rangés.</div>
          ) : (
            loose.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`admin-loose-${c.id}`}>
                <Link to={`/admin/cours/${c.id}/modifier`} className="flex-1 min-w-0 text-sm text-zinc-200 hover:text-emerald-300">{c.title}</Link>
                <LevelIndicator level={c.level} />
                <select
                  value=""
                  onChange={(e) => {
                    const t = themes.find((x) => x.id === e.target.value);
                    if (t) { setOpen((s) => new Set(s).add(t.id)); saveCourses(t.id, [...t.course_ids, c.id], "Concept rangé (ajouté à la fin de la thématique)"); }
                  }}
                  disabled={!themes.length}
                  className="h-9 rounded-md bg-white/[0.03] border border-white/10 px-2 text-sm text-zinc-300"
                  data-testid={`admin-loose-assign-${c.id}`}
                >
                  <option value="">{themes.length ? "Ranger dans…" : "Créez d'abord une thématique"}</option>
                  {themes.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
            ))
          )}
        </div>
      </div>
      {modal && <ThemeModal theme={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
    </div>
  );
}
